import {Capacitor} from "@capacitor/core";
import Clarity from "@microsoft/clarity";
import {createContext, useContext, useEffect, useMemo, useReducer} from "react";
import api, {apiAction, apiCall} from "./api";
import {config} from "./config/config.ts";
import {Player} from "./types";

const ACTIONS = {
    setToken:   "setToken",
    clearToken: "clearToken",
    setUser:    "setUser",
};

const authReducer = (state: any, action: any) => {
    switch (action.type) {
        case ACTIONS.setToken:
            return {...state, jwt_token: action.payload};

        case ACTIONS.clearToken:
            return {...state, jwt_token: null};

        case ACTIONS.setUser:
            return {...state, user: action.payload};

        default:
            console.error(`You passed an action.type: ${action.type} which doesn't exist`);
    }
};

if (!config.isCordova && !sessionStorage.landingUrl) {
    sessionStorage.landingUrl = document.location.href;
}

const initialData: {
    jwt_token: string,
    user?: null | Player // undefined - loading, null - unauthenticated // false causes typing issues
    initialized: boolean,
    unauthenticated: boolean,
    landingUrl: string,

    setToken: (_) => void,
    clearToken: () => void,
    setFieldsFromResponse: (_) => void,
    reloadUser: () => Promise<any>,
    signInWithEmailAndPassword: (_u: string, _p: string) => Promise<any>,
    signOut: () => void,
    makeSureUserExists: (_u: string, _p: string, _uid: string) => void,
} = {
    jwt_token:  localStorage.getItem("jwt_token"),
    landingUrl: (sessionStorage?.landingUrl || null),
} as any;

const AuthContext = createContext(initialData);

const JWTAuthProvider = ({children}) => {
    const [state, dispatch] = useReducer(authReducer, initialData);

    const setToken = (newToken: string | null) => {
        api.jwt_token = newToken || '';
        localStorage.setItem("jwt_token", newToken || '');

        dispatch({type: ACTIONS.setToken, payload: newToken});
    };
    const setUser = (newUser: string | null | false) => {
        api.user = newUser;
        if (api.user?.uid) {
            if (api.user?.is_subscribed || api.user?.meta?.disable_ads) {
                localStorage.disable_ads = 'y';
            } else {
                localStorage.removeItem('disable_ads');
            }
        }
        if (config.clarityProjectId && api.user?.email && Capacitor.getPlatform() !== 'ios') {
            Clarity.identify(api.user?.email);
        }
        dispatch({type: ACTIONS.setUser, payload: newUser});
    };
    const clearToken = () => {
        api.jwt_token = '';
        localStorage.removeItem("jwt_token");

        dispatch({type: ACTIONS.clearToken});
    };
    const setFieldsFromResponse = (res) => {
        if (res.access_token) {
            setToken(res.access_token);
            if (res.user) {
                setUser(res.user);
            }
        }
    };
    const reloadUser = async () => {
        return apiCall('auth/me')
            .then(async (res) => res.json())
            .then((res: any) => {
                if (res?.email) {
                    console.debug('auth/me/reload:', res);
                    setUser(res);
                }
            })
    };

    const signInWithEmailAndPassword = async (email: string, password: string) => {
        const res = await apiAction('auth/login', {email, password});
        if (res.access_token) {
            setToken(res.access_token);
            if (res.user) {
                setUser(res.user);
            }
            return {user: res.user};
        }
        return {};
    };
    const signOut = async () => {
        await apiAction('auth/logout');
        setToken('');
        setUser(null);
        sessionStorage.clear(); // clears sessionStorage.trial_ended_seen
    };
    const makeSureUserExists = async (email: string, password: string, uid: string) => {
        let res = await apiAction('auth/makeSureUserExists', {email, password, uid});
        if (res.user) {
            setUser(res.user);
        }
    };

    useEffect(() => {
        ;(async () => {
            if (!state.jwt_token) {
                setUser(null);
                return;
            }
            api.user = {};
            api.jwt_token = state.jwt_token;
            apiCall('auth/me')
                .then(async (res) => {
                    console.debug('res.status', res.status);
                    if (res.status == 401) {
                        console.info('JWT Token has expired.')
                        setToken('');
                        setUser(null);
                        return null;
                    }
                    return res.json()
                })
                .then((res: any) => {
                    if (res?.email) {
                        console.debug('auth/me:', res);
                        setUser(res);
                    }
                });
        })();

        const interval = setInterval(() => {
            if (!state.jwt_token) {
                return;
            }
            apiCall('auth/refresh')
                .then(async (res) => {
                    console.log(res);
                    return res.json()
                }, (err) => console.log('err', err))
                .then((res) => {
                    console.log('res', res);
                    if (res?.access_token) {
                        setToken(res.access_token);
                    }
                });
        }, config.jwtRefreshSeconds * 1000);

        return () => {
            clearInterval(interval);
        };
    }, []);

    const contextValue = useMemo(
        () => ({
            ...state,
            initialized:     (state.user !== undefined),
            unauthenticated: (state.user === null),
            setToken,
            clearToken,
            signInWithEmailAndPassword,
            reloadUser,
            signOut,
            makeSureUserExists,
            setFieldsFromResponse,
        }),
        [state]
    );

    return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useJwtAuth = () => {
    return useContext(AuthContext);
};

export const useJwtUser = () => {
    return useContext(AuthContext).user;
};

export default JWTAuthProvider;
